import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import SEO from '../components/SEO';
import { useWebsiteContent } from '../hooks/useWebsiteContent';
import { trackFormSubmission } from '../utils/analytics';
import './Contact.css';

const Contact = () => {
  const { content } = useWebsiteContent('contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // EmailJS configuration
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        throw new Error('EmailJS configuration is missing. Please check your environment variables.');
      }

      // Send email using EmailJS
      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          company: formData.company || 'Not provided',
          phone: formData.phone || 'Not provided',
          message: formData.message,
        },
        publicKey
      );

      setSubmitStatus('success');
      
      // Track successful form submission
      trackFormSubmission('contact_form');
      
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact">
      <SEO
        title="Contact MarketVendr - UK Import Services"
        description="Contact MarketVendr for UK import, distribution, and customs clearance services. Located in Grimsby, UK. Get a quote for importing products to the UK, including alcohol imports. Email: Contact@marketvendr.com | Phone: 01472 282216"
        keywords="contact MarketVendr, UK import services contact, UK distribution contact, UK customs clearance contact, import to UK quote, alcohol import UK contact, Grimsby import services"
        url="https://www.marketvendr.com/contact"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "mainEntity": {
            "@type": "Organization",
            "name": "MarketVendr",
            "telephone": "+44-1472-282216",
            "email": "Contact@marketvendr.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "53 Energy Parkway",
              "addressLocality": "Grimsby",
              "postalCode": "DN31 2TT",
              "addressCountry": "GB"
            }
          }
        }}
      />
      <section className="contact-hero">
        <div className="container">
          <h1>{content.contact_title || 'Contact Us'}</h1>
          <p>{content.contact_subtitle || 'Get in touch with our team - we\'re here to help'}</p>
        </div>
      </section>

      <section className="contact-content">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2>{content.contact_info_title || 'Get in Touch'}</h2>
              <p>
                {content.contact_info_desc || 'Have questions about our services? Want to discuss your logistics needs? We\'d love to hear from you.'}
              </p>

              <div className="contact-details">
                <div className="contact-item">
                  <h3>Email</h3>
                  <p><a href="mailto:Contact@marketvendr.com">Contact@marketvendr.com</a></p>
                </div>
                <div className="contact-item">
                  <h3>Phone</h3>
                  <p>01472 282216</p>
                </div>
                <div className="contact-item">
                  <h3>Address</h3>
                  <p>53 Energy Parkway</p>
                  <p>Grimsby</p>
                  <p>DN31 2TT</p>
                </div>
                <div className="contact-item">
                  <h3>Company Information</h3>
                  <p><strong>MARKET VENDR LIMITED</strong></p>
                  <p>AWRS NO: XQAW 000 0012 2069</p>
                  <p>Company Registration No: 08047025</p>
                  <p>VAT NO: 482 5075 80</p>
                  <p>EORI: GB482507580000</p>
                </div>
              </div>
            </div>

            <div className="contact-form-wrapper">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>

                {submitStatus === 'success' && (
                  <div className="form-message form-success">
                    Thank you for your message! We will get back to you soon.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="form-message form-error">
                    There was an error sending your message. Please try again or contact us directly at Contact@marketvendr.com
                  </div>
                )}
                <button type="submit" className="submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;

